import type { KitNote } from '../kit/kit';
import type { Department } from '../user/user';
import { isEvent } from './labNotes';

/**
 * Lab Notes as a conversation: which rows are drawn, in what order, under whose
 * name, and what a message sent from this screen says about itself while it is
 * on its way. The request of 5 October 2026 (third), section 4.
 *
 * The rule this file exists for: ONE message is ONE row, from the moment Send
 * is pressed until the page closes. A message used to be drawn by two rows, a
 * pending one keyed by the client's own id and then, once the server answered,
 * a different one keyed by the server's id. The swap was the flicker, the jump
 * and the short-lived duplicate the request reports. Here the row keeps the
 * client's key for good, and the server's copy is matched to it, so sending to
 * sent is a change of one field on a row that never leaves.
 */

/** The longest message the server keeps; it trims, then cuts here. */
export const LAB_NOTE_MAX = 2000;

/**
 * Whether two instants fall in the same clock minute, the HH:mm a row prints.
 * Lab time is a whole number of minutes from UTC, so the UTC minute is the
 * lab's minute too.
 */
export function sameMinute(a: string, b: string): boolean {
  return Math.floor(Date.parse(a) / 60000) === Math.floor(Date.parse(b) / 60000);
}

/** Where a message sent from this screen has got to. */
export type NoteDelivery = 'sending' | 'sent' | 'failed';

/** A message written on this screen, kept for as long as the screen is open. */
export interface OutgoingNote {
  /** The client's own id: the row's key, and the Idempotency-Key of the request. */
  clientId: string;
  /** As the server will keep it: trimmed and cut at LAB_NOTE_MAX. */
  text: string;
  visibleTo: { id: string; name: string } | null;
  /** When Send was pressed. The row keeps this time, so confirming never moves it. */
  sentAt: string;
  state: NoteDelivery;
  /** The server's id for it, once the request has answered. */
  noteId: string | null;
  /** The notes already in the thread when it was sent: none of them can be this one. */
  knownIds: readonly string[];
  /** Whether the row still carries its status line. See `quietDelivered`. */
  showsStatus: boolean;
}

/** Something the system recorded, drawn as a line across the conversation. */
export interface ConversationEvent {
  type: 'event';
  key: string;
  note: KitNote;
}

/** A message somebody wrote. */
export interface ConversationMessage {
  type: 'message';
  /** Stable for the life of the row: the client's id for one sent here, else the note's. */
  key: string;
  text: string;
  /** The time the row prints and is grouped by. */
  at: string;
  byUserId: string | null;
  byUserName: string;
  byUserDepartment: Department | null;
  visibleTo: { id: string; name: string } | null;
  mine: boolean;
  /** The first of a run by one person in one minute: it carries the name and the time. */
  first: boolean;
  /** The last of such a run. */
  last: boolean;
  /** Written on this screen since it was opened. */
  sentHere: boolean;
  /** What the status line under the row says, or null when it has none. */
  delivery: NoteDelivery | null;
}

export type ConversationRow = ConversationEvent | ConversationMessage;

type Draft = ConversationEvent | Omit<ConversationMessage, 'first' | 'last'>;

/** Whether `next` carries on the run `previous` is in: same person, same clock minute. */
function continues(previous: Draft | undefined, next: Draft | undefined): boolean {
  return (
    previous !== undefined &&
    next !== undefined &&
    previous.type === 'message' &&
    next.type === 'message' &&
    previous.byUserId !== null &&
    previous.byUserId === next.byUserId &&
    sameMinute(previous.at, next.at)
  );
}

/**
 * The rows of a conversation, oldest first.
 *
 * `items` is the thread as the server sends it, newest first. Reversing before
 * the stable sort keeps rows written in the same instant (an assignment and its
 * note) in the order they were written.
 *
 * A message sent here owns the server's note whose id the request answered
 * with. Until that answer arrives, or when it was lost on the way, a refetch
 * can deliver the note first; the message then adopts the oldest note of the
 * caller's own that was not in the thread when it was sent, says the same thing
 * to the same reader, and belongs to no other message. Either way the note is
 * drawn by the message's own row, under the client's key and at the time Send
 * was pressed, and reads as sent whatever the request itself came to.
 *
 * Messages with no note yet follow the server's rows, in the order they were
 * sent.
 *
 * Consecutive messages by one person within one clock minute are one group:
 * the first carries the name and the time. An event, another person, a note
 * nobody signed, or the next minute starts a new one. Privacy does not: each
 * private message says so on its own bubble.
 */
export function conversationRows(
  items: readonly KitNote[],
  outgoing: readonly OutgoingNote[],
  meId: string,
): ConversationRow[] {
  const notes = [...items].reverse().sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  // By the server's id first, so no message adopts a note another one is known to own.
  const owners = new Map<string, OutgoingNote>();
  for (const entry of outgoing) {
    if (entry.noteId !== null) owners.set(entry.noteId, entry);
  }
  for (const entry of outgoing) {
    if (entry.noteId !== null) continue;
    const reader = entry.visibleTo?.id ?? null;
    const adopted = notes.find(
      (note) =>
        !owners.has(note.id) &&
        !isEvent(note) &&
        note.byUserId === meId &&
        !entry.knownIds.includes(note.id) &&
        note.text === entry.text &&
        (note.visibleTo?.id ?? null) === reader,
    );
    if (adopted !== undefined) owners.set(adopted.id, entry);
  }

  const drafts: Draft[] = [];
  const drawn = new Set<string>();
  for (const note of notes) {
    if (isEvent(note)) {
      drafts.push({ type: 'event', key: note.id, note });
      continue;
    }
    const owner = owners.get(note.id);
    if (owner === undefined) {
      drafts.push({
        type: 'message',
        key: note.id,
        text: note.text,
        at: note.createdAt,
        byUserId: note.byUserId,
        byUserName: note.byUserName,
        byUserDepartment: note.byUserDepartment,
        visibleTo: note.visibleTo,
        mine: note.byUserId === meId,
        sentHere: false,
        delivery: null,
      });
      continue;
    }
    drawn.add(owner.clientId);
    drafts.push({
      type: 'message',
      key: owner.clientId,
      text: note.text,
      at: owner.sentAt,
      byUserId: note.byUserId,
      byUserName: note.byUserName,
      byUserDepartment: note.byUserDepartment,
      visibleTo: note.visibleTo,
      mine: true,
      sentHere: true,
      delivery: owner.showsStatus ? 'sent' : null,
    });
  }
  for (const entry of outgoing) {
    if (drawn.has(entry.clientId)) continue;
    drafts.push({
      type: 'message',
      key: entry.clientId,
      text: entry.text,
      at: entry.sentAt,
      byUserId: meId,
      byUserName: '',
      byUserDepartment: null,
      visibleTo: entry.visibleTo,
      mine: true,
      sentHere: true,
      delivery: entry.showsStatus ? entry.state : null,
    });
  }

  return drafts.map((row, index) =>
    row.type === 'event'
      ? row
      : {
          ...row,
          first: !continues(drafts[index - 1], row),
          last: !continues(row, drafts[index + 1]),
        },
  );
}

/**
 * Takes the status line off every message that has arrived, for the moment the
 * next one is sent: "Sent" stays under the newest message until then. A message
 * still on its way, or one that failed, keeps its line.
 *
 * It is done at that moment and at no other because the line has a height.
 * Removing it while nothing else changes would pull the rows under it up by a
 * line; at the next send a new row arrives anyway, and the list moves once.
 *
 * `delivered` names the messages whose note is in the thread although their
 * own request never said so (see `conversationRows`): they read as sent, so
 * they are quietened too.
 */
export function quietDelivered(
  outgoing: readonly OutgoingNote[],
  delivered: readonly string[] = [],
): OutgoingNote[] {
  return outgoing.map((entry) =>
    entry.showsStatus && (entry.state === 'sent' || delivered.includes(entry.clientId))
      ? { ...entry, showsStatus: false }
      : entry,
  );
}
